/**
 * Coordinates the static splash overlay defined in `src/index.html`.
 *
 * The splash is rendered by the browser before any bundle loads; React's only
 * job is to dismiss it at the right moment:
 *  - generic routes: on app-shell mount (Clerk has loaded by then)
 *  - editor routes: when Pixi init settles, via <SplashDismisser />
 */

const SPLASH_ID = 'splash';
const FADE_OUT_CLASS = 'splash-fade-out';
/** Must be at least the #splash opacity transition duration in index.html. */
const FADE_OUT_MS = 300;
/** Upper bound on how long we hold the splash waiting for web fonts, so a
 *  stalled/failed font fetch can never strand the splash. */
const FONTS_READY_CAP_MS = 3000;

/**
 * Resolves once the document's web fonts have finished loading, or after
 * `capMs`, whichever comes first. Returns null where the FontFaceSet API is
 * unavailable (nothing to wait for — lets {@link dismissSplash} keep a
 * synchronous fast-path).
 *
 * The self-hosted body font (DM Sans) ships in the bundle CSS with
 * `font-display: swap`, so without this gate text paints in the system
 * fallback first and visibly swaps to DM Sans once the woff2 downloads (FOUT).
 * Holding the splash until fonts are ready makes that swap happen under the
 * overlay, so the page reveals already in the correct font.
 *
 * Two races make passive waiting (`fonts.status` / `fonts.ready` at call
 * time) insufficient — both let the swap land after the reveal (the
 * home-page jitter):
 *
 *  1. The browser requests a web font lazily, during the style/layout pass
 *     that first paints text needing it. At the moment React's mount effect
 *     calls dismissSplash() that pass has not happened yet, so `fonts.status`
 *     is trivially 'loaded' with zero fetches in flight. → Explicitly
 *     `fonts.load()` the app-wide body faces so their fetches start now,
 *     deterministically, and cover text that mounts after dismissal too.
 *  2. Text committed around dismissal (route content, late-mounting widgets)
 *     kicks off additional fetches — e.g. the zh-TW/ja unicode-range CJK
 *     subsets, which can't be requested up front because which subset files
 *     are needed depends on the glyphs actually painted. → Sample the
 *     FontFaceSet only after two animation frames (one full rendered frame),
 *     by which point those lazy fetches are in flight and `fonts.ready`
 *     genuinely tracks them.
 *
 * The cap timer starts immediately, so a hidden tab (where rAF never fires)
 * or a stalled fetch can never strand the splash.
 */
const APP_FONT_FACES = [
    '400 1em "DM Sans"',
    '500 1em "DM Sans"',
    '600 1em "DM Sans"',
    '700 1em "DM Sans"',
    'italic 400 1em "DM Sans"',
];

function fontsReadyWithin(capMs: number): Promise<void> | null {
    const fonts: FontFaceSet | undefined = document.fonts;
    if (!fonts) return null;
    return new Promise<void>((resolve) => {
        let settled = false;
        const done = () => {
            if (settled) return;
            settled = true;
            resolve();
        };
        const explicitLoads = APP_FONT_FACES.map((face) =>
            fonts.load(face).then(
                () => undefined,
                () => undefined,
            ),
        );
        requestAnimationFrame(() =>
            requestAnimationFrame(() => {
                const lazyLoads = fonts.ready.then(
                    () => undefined,
                    () => undefined,
                );
                void Promise.all([...explicitLoads, lazyLoads]).then(done);
            }),
        );
        window.setTimeout(done, capMs);
    });
}

/**
 * Whether this pathname is an editor route whose splash must persist until
 * Pixi init settles. Exact-segment match: `/crochet-icons` shares a prefix
 * with `/crochet` but is NOT an editor route.
 *
 * `/crochet` (and `/crochet/`) is the diagram gallery — a plain, near-instant
 * page with no Pixi canvas — so it is deliberately NOT an editor path. Only
 * `/crochet/<diagramId>` (a non-empty segment after `/crochet/`) is the Pixi
 * editor route. Same story for `/grid-editor`: the bare path (and trailing
 * slash) is the grid diagram gallery; only `/grid-editor/<diagramId>` is the
 * Pixi editor route. The shared-viewer routes `/s/<token>` and
 * `/shared/<shareId>` (and the crochet view-only route `/crochet/<id>/view`,
 * already covered by the `/crochet/` prefix) also mount a Pixi canvas.
 *
 * The `src/index.html` variant picker mirrors ONLY the crochet/grid branches
 * (those get a branded splash); the shared routes fall through to the generic
 * variant there while still counting as editor paths here (persist the splash
 * until Pixi settles).
 */
export function isEditorPath(pathname: string): boolean {
    return (
        (pathname.startsWith('/crochet/') &&
            pathname.length > '/crochet/'.length) ||
        (pathname.startsWith('/grid-editor/') &&
            pathname.length > '/grid-editor/'.length) ||
        (pathname.startsWith('/s/') && pathname.length > '/s/'.length) ||
        (pathname.startsWith('/shared/') && pathname.length > '/shared/'.length)
    );
}

/**
 * Fade out and remove the splash overlay. Idempotent; safe to call anytime.
 *
 * Honors the minimum visible time stamped by the inline script in
 * `src/index.html` (`data-shown-at` / `data-min-visible-ms`, per variant) so
 * the loading animation always reads as complete before the fade starts, even
 * when the page is ready sooner. Missing or malformed stamps fade immediately.
 */
export function dismissSplash(): void {
    const el = document.getElementById(SPLASH_ID);
    if (el === null || el.dataset.dismissing === 'true') return;
    el.dataset.dismissing = 'true';

    const shownAt = Number(el.dataset.shownAt);
    const minVisibleMs = Number(el.dataset.minVisibleMs);
    const remaining =
        Number.isFinite(shownAt) && Number.isFinite(minVisibleMs)
            ? Math.max(0, minVisibleMs - (performance.now() - shownAt))
            : 0;

    const startFade = () => {
        el.classList.add(FADE_OUT_CLASS);
        window.setTimeout(() => {
            el.remove();
            // Drop the marker the index.html inline script set while the
            // splash was visible: its `html[data-splash]` rule suppresses the
            // window scrollbar (transient overflow during canvas init would
            // otherwise flash a scrollbar and shift the centered splash) and
            // paints the canvas in the splash color (so the reserved
            // scrollbar gutter beside the splash doesn't show the default
            // white page canvas).
            document.documentElement.removeAttribute('data-splash');
        }, FADE_OUT_MS);
    };
    // Hold the fade until BOTH gates clear: the per-variant minimum visible
    // time (so the animation reads as complete) AND web fonts being ready (so
    // the DM Sans swap happens under the overlay rather than as a visible
    // flash after reveal).
    //
    // Synchronous fast-path when there is no gate to wait on — no min-visible
    // time remaining and no FontFaceSet API at all. This keeps environments
    // without web fonts (tests) from deferring the fade onto a later tick.
    const fontsGate = fontsReadyWithin(FONTS_READY_CAP_MS);
    if (remaining === 0 && fontsGate === null) {
        startFade();
        return;
    }
    const minVisible = new Promise<void>((resolve) =>
        window.setTimeout(resolve, remaining),
    );
    void Promise.all([minVisible, fontsGate ?? Promise.resolve()]).then(
        startFade,
    );
}

/** Route IDs (from routeTree.gen.ts) whose components own splash dismissal
 *  via <SplashDismisser /> (or, for a failed shared load, dismiss the splash
 *  directly — see SharedCrochetViewerPage). The per-diagram editor routes plus
 *  the view-only routes: crochet/grid editors, the crochet and grid view-only
 *  routes, and the shared-viewer routes. `/crochet/` and `/grid-editor/` bare paths are the
 *  galleries (plain pages, no canvas) and are intentionally absent. */
const EDITOR_ROUTE_IDS = [
    '/crochet/$diagramId',
    '/grid-editor/$diagramId',
    '/crochet/$diagramId_/view',
    '/grid-editor/$diagramId_/view',
    '/s/$token',
    '/shared/$shareId',
];

/** Minimal structural slice of the TanStack Router instance we depend on. */
type SplashRouter = {
    state: {
        matches: ReadonlyArray<{
            routeId: string;
            /** Set on the root match when no route matched the URL exactly. */
            globalNotFound?: boolean;
            status?: string;
        }>;
    };
    subscribe(event: 'onResolved', callback: () => void): () => void;
};

/**
 * Shell-side splash dismissal, called once when the app shell mounts.
 *
 * Non-editor paths: ready as soon as the shell mounts — dismiss now.
 * Editor paths: the editor's <SplashDismisser /> owns dismissal, BUT the
 * editor route may never mount — a beforeLoad redirect (signed-out →
 * /sign-in) or an editor-prefixed URL resolving to not-found — which would
 * strand the splash. Watch navigation and dismiss whenever a resolution
 * does not actually match an editor route.
 *
 * A not-found resolution can still LIST an editor route: TanStack fuzzy-matches
 * the nearest parent (`/crochet/<id>/xyz` → `/crochet/$diagramId`) and flags the
 * root match `globalNotFound`, and the root then renders NotFound instead of the
 * editor. Such a resolution counts as NOT an editor route.
 *
 * Returns the unsubscribe function when a subscription was made (idempotency
 * of dismissSplash makes a lingering subscription harmless either way).
 */
export function dismissSplashOnShellReady(
    pathname: string,
    router: SplashRouter,
): (() => void) | undefined {
    if (!isEditorPath(pathname)) {
        dismissSplash();
        return undefined;
    }
    return router.subscribe('onResolved', () => {
        const { matches } = router.state;
        const notFound = matches.some(
            (match) =>
                match.globalNotFound === true || match.status === 'notFound',
        );
        const matchedEditor =
            !notFound &&
            matches.some((match) => EDITOR_ROUTE_IDS.includes(match.routeId));
        if (!matchedEditor) dismissSplash();
    });
}
